import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { CtaButton } from "@/components/common/CtaButton";
import { Counter } from "@/components/common/Counter";
import { ConceptBadge } from "@/components/common/ConceptBadge";
import { DetailCta } from "@/components/detail/DetailCta";
import { caseStudies } from "@/data/caseStudies.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { ArrowUpRight, Clock } from "@/icons";
import { siteConfig } from "@/config/site.config";

export default function CaseStudies() {
  return (
    <>
      <Seo
        title="Transformation Portfolio | VibeWell"
        description="Explore VibeWell's AI-powered digital transformation portfolio featuring enterprise platforms, workflow automation, healthcare, ERP, CRM, retail, education and logistics concept solutions with measurable business outcomes."
        path="/case-studies"
        image="/images/seo/transformation-portfolio-og.png"
      />
      <PageHero
        eyebrow="Transformation Portfolio"
        titleLines={["Transforming", "Businesses with AI."]}
        description="Explore concept transformation projects demonstrating how VibeWell combines AI, workflow automation, cloud-native engineering and exceptional user experience to solve complex business challenges across industries."
      >
        <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
          Book a Free Consultation
        </CtaButton>
      </PageHero>

      <Section spacing="compact" className="pt-0">
        <Container>
          <motion.div
            variants={staggerContainer(0.1, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="flex flex-col gap-6"
          >
            {caseStudies.map((cs) => (
              <motion.div key={cs.slug} variants={fadeUp}>
                <Link
                  to={`/case-studies/${cs.slug}`}
                  data-testid={`case-study-card-${cs.slug}`}
                  className="group grid overflow-hidden rounded-[1.5rem] border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_-32px_rgba(15,23,42,0.3)] lg:grid-cols-[1fr_1.1fr]"
                >
                  {/* Visual */}
                  <div className="relative min-h-[220px] overflow-hidden p-8">
                    <div
                      className="absolute inset-0"
                      style={{ background: `linear-gradient(135deg, ${cs.accent}1f, ${cs.accent}05)` }}
                    />
                    <div className="absolute inset-0 bg-grid opacity-40" />

                    <div className="relative flex h-full flex-col justify-between">

                      <ConceptBadge accent={cs.accent} />

                      <div className="mt-6 rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">

                          <p
                              className="text-sm leading-6"
                              style={{
                                  color: cs.accent,
                              }}
                          >
                              {cs.outcomeHighlight}
                          </p>

                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-4">

                          {cs.results.slice(0, 2).map((result) => (

                              <div
                                  key={result.label}
                                  className="rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"
                              >

                                  <Counter
                                      value={result.value}
                                      className="font-heading text-2xl font-bold"
                                  />

                                  <p className="mt-2 text-xs text-muted-foreground">
                                      {result.label}
                                  </p>

                              </div>

                          ))}

                      </div>

                    </div>

                    {/* <div className="relative flex h-full flex-col justify-between">
                      <ConceptBadge accent={cs.accent} />
                      <div className="mt-8 flex gap-6">
                        {cs.results.slice(0, 2).map((r) => (
                          <div key={r.label}>
                            <Counter
                              value={r.value}
                              className="font-heading text-3xl font-extrabold tracking-tight"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">{r.label}</p>
                          </div>
                        ))}
                      </div>
                    </div> */}
                  </div>
                  {/* Content */}
                  <div className="flex flex-col p-8">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                      <span>{cs.category}</span>
                      <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                      <span className="text-muted-foreground">{cs.industry}</span>
                    </div>
                    <h2 className="mt-2 font-heading text-2xl font-bold text-foreground">
                      {cs.title}
                    </h2>

                    <div className="mt-3">
                      <span
                          className="inline-flex rounded-full px-3 py-1 text-xs font-semibold"
                          style={{
                              backgroundColor: `${cs.accent}18`,
                              color: cs.accent,
                          }}
                      >
                          {cs.transformation}
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-7 text-muted-foreground">
                      {cs.cardSummary}
                    </p>
                    {/* <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {cs.overview}
                    </p> */}
                    <div className="mt-5 flex items-center justify-between">
                      {/* <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" /> {cs.duration}
                      </span> */}
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full border border-border px-3 py-1 text-xs">
                            AI
                        </span>

                        <span className="rounded-full border border-border px-3 py-1 text-xs">
                            Automation
                        </span>

                        <span className="rounded-full border border-border px-3 py-1 text-xs">
                            Cloud Native
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-300 group-hover:gap-2.5">
                        Explore case study <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <DetailCta />
    </>
  );
}
